import { type MouseHitTarget, type MouseInputAdapter } from '@databases/selections';

/**
 * A null result continues resolution; a `none` target stops it.
 */
export type HitTargetResolver = (
  element: Element,
  clientX: number,
  clientY: number,
) => MouseHitTarget | null;

type GridCoord = Extract<MouseHitTarget, { type: 'row' }>['row'];

export function parseGridCoord(value: string | null): GridCoord | null {
  if (value === null || !/^\d+$/.test(value)) {
    return null;
  }

  const index = Number.parseInt(value, 10);

  return Number.isSafeInteger(index) ? { index } : null;
}

/**
 * Renderers own cell eligibility; coordinate validation is shared.
 */
export function resolveCellCoordinates(cell: Element): MouseHitTarget {
  const row = parseGridCoord(cell.getAttribute('data-row-index'));
  const col = parseGridCoord(cell.getAttribute('data-col-index'));

  return row && col ? { type: 'cell', position: { row, col } } : { type: 'none' };
}

export function getDatabaseScrollContainer(): HTMLElement | null {
  return document.getElementById('scrollable-inner-container');
}

export const resolveLayoutShell: HitTargetResolver = (element) =>
  element.closest(
    '#scrollable-inner-container, [data-testid="layout-view--wrapper"], #layout-container',
  )
    ? { type: 'outside' }
    : null;

/**
 * DOM plumbing shared by renderer-specific, ordered hit-test rules.
 */
export function createDomMouseInputAdapter(
  resolvers: readonly HitTargetResolver[],
  getHorizontalAutoScrollViewportLeft: () => number | null = () => null,
): MouseInputAdapter {
  return {
    getScrollContainer: getDatabaseScrollContainer,
    getHorizontalAutoScrollViewportLeft,
    hitTest(clientX, clientY) {
      const element = document.elementFromPoint(clientX, clientY);

      if (element) {
        for (const resolve of resolvers) {
          const target = resolve(element, clientX, clientY);

          if (target) {
            return target;
          }
        }
      }

      return { type: 'none' };
    },
  };
}
